import { createCursor, type QueryFilterOperator } from '@pkg/api/query'
import { environmentManager } from '@tanstack/react-query'
import type { TableNode } from 'kysely'
import { type ExpressionBuilder, type ReferenceExpression, type SelectQueryBuilder, type SqlBool, sql } from 'kysely'
import stopwords from 'stopwords-iso' with { type: 'json' }
import { normalizeSortDirection } from './query.utils'

// ── Types ──────────────────────────────────────────────────────────────────

export type SortDirection = 'asc' | 'desc'
export type InputSortDirection = SortDirection | 'ascend' | 'descend'
export type DeleteFilter = 'only' | 'exclude' | 'include' | true | false
export type FilterValue = string | number | boolean | Date | ReadonlyArray<string | number | boolean | Date> | null
export const PaginationMode = { cursor: 'cursor', offset: 'offset' } as const
export type PaginationModeType = (typeof PaginationMode)[keyof typeof PaginationMode]

export type FlatFilterOperator = Exclude<QueryFilterOperator, 'between'>

export type FlatFilterItem<K extends string = string> = {
  field: K
  operator: FlatFilterOperator
  value: FilterValue
}

export type QueryParams = {
  filters: ReadonlyArray<FlatFilterItem>
  sortBy: string | null
  sortDir: SortDirection
  pageSize: number
  page: number
  cursorToken?: string
  paginationMode: PaginationModeType
  searchQueryText?: string
  deleted: DeleteFilter
}

// ── parseQuery ─────────────────────────────────────────────────────────────

export function parseQuery(
  input: Record<string, unknown> & { filters?: unknown },
  defaults?: { paginationMode?: PaginationModeType },
): QueryParams {
  const { sortBy, sortDirection, pageSize, page, cursor, searchValue, filters, deleted, paginationMode } =
    input as Record<string, unknown>
  return {
    sortBy: (sortBy as string | undefined) ?? null,
    sortDir: normalizeSortDirection(sortDirection as InputSortDirection | undefined),
    pageSize: Number(pageSize) > 0 ? Number(pageSize) : 25,
    page: Number(page) > 0 ? Number(page) : 1,
    cursorToken: typeof cursor === 'string' ? cursor : undefined,
    paginationMode:
      (paginationMode as PaginationModeType | undefined) ?? defaults?.paginationMode ?? PaginationMode.cursor,
    searchQueryText: typeof searchValue === 'string' ? searchValue : undefined,
    filters: flattenFilters(filters),
    deleted: (deleted as DeleteFilter | undefined) ?? 'exclude',
  }
}

// ── $call helpers ──────────────────────────────────────────────────────────

export function filters<DB, TB extends keyof DB>(
  params: QueryParams,
  keys?: string | readonly string[],
  ref?: ReferenceExpression<DB, TB>,
): <O>(qb: SelectQueryBuilder<DB, TB, O>) => SelectQueryBuilder<DB, TB, O> {
  return qb => {
    const keyArr = keys ? (Array.isArray(keys) ? keys : [keys]) : null
    const subset = keyArr ? params.filters.filter(f => keyArr.includes(f.field)) : params.filters
    return subset.reduce((acc, f) => applyFilterToBuilder(acc, (ref ?? f.field) as ReferenceExpression<DB, TB>, f), qb)
  }
}

export function deleted<DB, TB extends keyof DB>(
  value: DeleteFilter,
  ref?: ReferenceExpression<DB, TB>,
): <O>(qb: SelectQueryBuilder<DB, TB, O>) => SelectQueryBuilder<DB, TB, O> {
  return qb => {
    const col = (ref ?? 'deletedAt') as ReferenceExpression<DB, TB>
    if (value === 'only' || value === true) return applyFilterToBuilder(qb, col, { operator: 'ne', value: null })
    if (value === 'exclude' || value === false) return applyFilterToBuilder(qb, col, { operator: 'eq', value: null })
    return qb
  }
}

export function sort<DB, TB extends keyof DB>(
  params: QueryParams,
): <O>(qb: SelectQueryBuilder<DB, TB, O>) => SelectQueryBuilder<DB, TB, O> {
  return qb => {
    if (!params.sortBy) return qb

    let sortBuilder = sql`lower(${sql.ref(params.sortBy)})`

    // converts nested JSON values from dot notation ("table.column.nestedProperty1.nestedProperty2")
    // to SQLite extraction syntax ("table"."column" ->> '$.nestedProperty1.nestedProperty2')
    if (/.*\..*\..*/g.test(params.sortBy)) {
      const parts = params.sortBy.split('.')
      const column = `${parts.splice(0, 1)}.${parts.splice(0, 1)}`
      const path = `$.${parts.join('.')}`

      sortBuilder = sql`${sql.ref(column)} ->> ${sql.lit(path)}`
    }

    return qb.orderBy(sortBuilder as never, params.sortDir)
  }
}

export function paginate<DB, TB extends keyof DB>(
  params: QueryParams,
): <O>(qb: SelectQueryBuilder<DB, TB, O>) => SelectQueryBuilder<DB, TB, O> {
  return qb => {
    if (params.paginationMode === PaginationMode.offset) {
      return qb.limit(params.pageSize).offset((params.page - 1) * params.pageSize)
    }
    if (!params.sortBy) return qb.limit(params.pageSize)
    const cursor = createCursor({
      sortBy: params.sortBy,
      sortDirection: params.sortDir,
      pageSize: params.pageSize,
      cursor: params.cursorToken,
    })
    const sortRef = sql`lower(${sql.ref(params.sortBy)})`
    if (cursor.current) {
      const op = params.sortDir === 'asc' ? '>' : '<'
      return qb
        .where(eb => (eb as never)(sortRef as never, op, cursor.current?.id as never))
        .orderBy(sortRef as never, params.sortDir)
        .limit(params.pageSize)
    }
    return qb.orderBy(sortRef as never, params.sortDir).limit(params.pageSize)
  }
}

export function search<DB, TB extends keyof DB>(
  params: QueryParams,
  vectorRef?: string | ((qb: SelectQueryBuilder<DB, TB, unknown>) => string),
): <O>(qb: SelectQueryBuilder<DB, TB, O>) => SelectQueryBuilder<DB, TB, O> {
  return qb => {
    if (!params.searchQueryText) return qb

    if (!environmentManager.isServer()) {
      const tableName =
        typeof vectorRef === 'string'
          ? vectorRef
          : (qb.toOperationNode().from?.froms?.[0] as TableNode | undefined)?.table.identifier.name
      if (!tableName) return qb
      const ftsTable = `${tableName}_fts`
      const term = prepareSqliteSearchTerm(params.searchQueryText)
      if (!term) return qb
      return (
        qb
          .innerJoin(ftsTable as never, sql.raw(`${ftsTable}.id`) as never, sql.raw(`${tableName}.id`) as never)
          // Target the joined `search` column explicitly to avoid double-counting BM25
          // across the per-field columns also indexed in `${ftsTable}`.
          .where(sql.ref(ftsTable) as never, 'match' as never, sql.lit(`search : ${term}`) as never)
          .orderBy('rank', 'asc')
      )
    }

    const ref = typeof vectorRef === 'function' ? vectorRef(qb as never) : (vectorRef ?? 'searchVector')
    const tsq = sql`to_tsquery('german', ${preparePostgresSearchTerm(params.searchQueryText)})`
    return qb
      .select(sql`(ts_rank_cd(${sql.ref(ref)}, ${tsq}, 32) * 10 + ts_rank(${sql.ref(ref)}, ${tsq}, 1))`.as('rank'))
      .where(sql`${sql.ref(ref)} @@ ${tsq}` as never)
      .orderBy('rank' as never, 'desc')
  }
}

/** Apply filters + search + sort + paginate in one `$call` (replaces `applyDefaults`). */
export function all<DB, TB extends keyof DB>(
  params: QueryParams,
  vectorRef?: string | ((qb: SelectQueryBuilder<DB, TB, unknown>) => string),
): <O>(qb: SelectQueryBuilder<DB, TB, O>) => SelectQueryBuilder<DB, TB, O> {
  return qb => qb.$call(filters(params)).$call(search(params, vectorRef)).$call(sort(params)).$call(paginate(params))
}

/** Build a condition for a single filter key — for use inside `.where(eb => ...)`. */
export function filterCondition<DB, TB extends keyof DB>(
  params: QueryParams,
  key: string,
  eb: ExpressionBuilder<DB, TB>,
  ref?: ReferenceExpression<DB, TB>,
): SqlBool | null {
  const f = params.filters.find(item => item.field === key)
  if (!f) return null
  return buildCondition(eb, (ref ?? key) as ReferenceExpression<DB, TB>, f)
}

// ── Execute helpers ────────────────────────────────────────────────────────

export async function executePaginated<O>(
  qb: SelectQueryBuilder<unknown, never, O>,
  params: QueryParams,
): Promise<{
  data: O[]
  total: number
  page: number
  pageSize: number
  nextPageCursor: string | null
}> {
  const [rows, total] = await Promise.all([qb.execute(), count(qb)])
  let nextPageCursor: string | null = null
  if (params.paginationMode !== PaginationMode.offset && params.sortBy) {
    const cursor = createCursor({
      sortBy: params.sortBy,
      sortDirection: params.sortDir,
      pageSize: params.pageSize,
      cursor: params.cursorToken,
    })
    nextPageCursor = cursor.next(rows as never)
  }
  return { data: rows, total, page: params.page, pageSize: params.pageSize, nextPageCursor }
}

export async function count(qb: SelectQueryBuilder<unknown, never, unknown>): Promise<number> {
  const result = await qb
    .clearOffset()
    .clearLimit()
    .clearSelect()
    .clearGroupBy()
    .clearOrderBy()
    .select(eb => (eb as never).fn.countAll().as('total'))
    .executeTakeFirst()
  return Number((result as { total?: unknown })?.total ?? 0)
}

// ── Private helpers (verbatim from queryLifecycle.ts) ─────────────────────

function flattenFilters(filters?: unknown): ReadonlyArray<FlatFilterItem> {
  if (!filters || typeof filters !== 'object') return []
  const out: FlatFilterItem[] = []
  const entries = Object.entries(filters as Record<string, unknown>)
  for (const [field, val] of entries) {
    pushItem(field, val, out)
  }
  return out
}

function pushItem(field: string, val: unknown, out: FlatFilterItem[]) {
  if (val === undefined || val === null) return
  if (isOpEntry(val)) {
    if (val.operator === 'between') {
      const range = val.value as unknown as { from: FilterValue; to: FilterValue } | null | undefined
      if (range?.from !== undefined && range?.from !== null) {
        out.push({ field, operator: 'gte', value: range.from })
      }
      if (range?.to !== undefined && range?.to !== null) {
        out.push({ field, operator: 'lte', value: range.to })
      }
      return
    }
    out.push({ field, operator: val.operator as FlatFilterOperator, value: val.value })
    return
  }
  if (Array.isArray(val)) {
    out.push({ field, operator: 'in', value: val as FilterValue })
    return
  }
  const t = typeof val
  if (t === 'string' || t === 'number' || t === 'boolean') {
    out.push({ field, operator: 'eq', value: val as FilterValue })
    return
  }
  if (val instanceof Date) {
    out.push({ field, operator: 'eq', value: val })
  }
}

function isOpEntry(x: unknown): x is { operator: QueryFilterOperator; value: FilterValue } {
  return (
    !!x &&
    typeof x === 'object' &&
    'operator' in (x as Record<string, unknown>) &&
    'value' in (x as Record<string, unknown>)
  )
}

function expandJsonPath<DB, TB extends keyof DB>(ref: ReferenceExpression<DB, TB>): ReferenceExpression<DB, TB> {
  if (typeof ref !== 'string') return ref
  if (!/.*\..*\..*/g.test(ref)) return ref
  const parts = ref.split('.')
  const column = `${parts[0]}.${parts[1]}`
  const path = `$.${parts.slice(2).join('.')}`
  return sql`${sql.ref(column)} ->> ${sql.lit(path)}` as ReferenceExpression<DB, TB>
}

function applyFilterToBuilder<DB, TB extends keyof DB, O>(
  qb: SelectQueryBuilder<DB, TB, O>,
  ref: ReferenceExpression<DB, TB>,
  f: Omit<FlatFilterItem, 'field'>,
): SelectQueryBuilder<DB, TB, O> {
  ref = expandJsonPath(ref)
  switch (f.operator) {
    case 'eq':
      return f.value === null ? qb.where(ref, 'is', null) : qb.where(ref, '=', f.value)
    case 'ne':
      return f.value === null ? qb.where(ref, 'is not', null) : qb.where(ref, '<>', f.value)
    case 'gt':
      return qb.where(ref, '>', f.value)
    case 'gte':
      return qb.where(ref, '>=', f.value)
    case 'lt':
      return qb.where(ref, '<', f.value)
    case 'lte':
      return qb.where(ref, '<=', f.value)
    case 'like': {
      if (typeof f.value !== 'string') throw new Error('LIKE operator requires a string value')
      return qb.where(ref, 'like', `%${f.value}%`)
    }
    case 'in': {
      if (!Array.isArray(f.value)) throw new Error('IN operator requires an array value')
      return qb.where(ref, 'in', f.value)
    }
  }
}

function buildCondition<DB, TB extends keyof DB>(
  eb: ExpressionBuilder<DB, TB>,
  ref: ReferenceExpression<DB, TB>,
  f: FlatFilterItem,
): SqlBool {
  ref = expandJsonPath(ref)
  switch (f.operator) {
    case 'eq':
      return eb(ref, '=', f.value) as SqlBool
    case 'ne':
      return eb(ref, '<>', f.value) as SqlBool
    case 'gt':
      return eb(ref, '>', f.value) as SqlBool
    case 'gte':
      return eb(ref, '>=', f.value) as SqlBool
    case 'lt':
      return eb(ref, '<', f.value) as SqlBool
    case 'lte':
      return eb(ref, '<=', f.value) as SqlBool
    case 'like': {
      if (typeof f.value !== 'string') throw new Error('LIKE operator requires a string value')
      return eb(ref, 'like', `%${f.value}%`) as SqlBool
    }
    case 'in': {
      if (!Array.isArray(f.value)) throw new Error('IN operator requires an array value')
      return eb(ref, 'in', f.value) as SqlBool
    }
  }
}

// German stopwords that should be filtered out
const germanStopwords = new Set(stopwords.de)

function preparePostgresSearchTerm(raw?: string | null): string | undefined {
  const value = typeof raw === 'string' ? raw.trim() : ''
  if (!value) return undefined

  const tokens = value
    .split(/\s+/)
    .map(t => t.replace(/[^a-zA-ZäöüÄÖÜß0-9]/g, ''))
    .filter(t => t.length > 0)
    .filter(t => !germanStopwords.has(t.toLowerCase()))

  if (tokens.length === 0) return undefined

  // Use AND logic for better precision, prefix-match all tokens
  const processed = tokens.map(t => `${t}:*`).join(' & ')
  return processed.length > 0 ? processed : undefined
}

function prepareSqliteSearchTerm(raw?: string | null): string | undefined {
  const value = typeof raw === 'string' ? raw.trim() : ''
  if (!value) return undefined
  const processed = value
    .split(/\s+/)
    .map(t => t.replace(/[^a-zA-ZäöüÄÖÜß0-9]/g, ''))
    .filter(t => t.length > 0)
    .map(t => `${t}*`)
    .join(' ')
  return processed.length > 0 ? processed : undefined
}
